<template>
  <div ref="editorRootRef" class="telegram-dual-editor">
    <div v-if="currentMode === 'markdown'">
      <textarea
        ref="textareaRef"
        :value="modelValue"
        :placeholder="placeholder"
        :style="textareaStyle"
        :disabled="disabled"
        @input="handleTextareaInput"
        @keydown="handleMarkdownKeyDown"
        @paste="handleMarkdownPaste"
        @contextmenu.prevent.stop="markdownContextMenu.handleContextMenu"
      ></textarea>
      <markdown-context-menu
        :show="markdownContextMenu.showMenu"
        :position="markdownContextMenu.menuPosition"
        :has-selection="markdownContextMenu.hasSelection"
        @format="handleMarkdownFormat"
        @action="handleMarkdownAction"
      />
      <mention-autocomplete
        :show="userMentions.showAutocomplete"
        :position="userMentions.autocompletePosition"
        :filtered-members="userMentions.filteredMembers"
        :selected-index="userMentions.selectedIndex"
        @select="handleUserMentionSelect"
        @hover="handleUserMentionHover"
      />
      <task-autocomplete
        :show="taskMentions.showAutocomplete"
        :position="taskMentions.autocompletePosition"
        :filtered-tasks="taskMentions.filteredTasks"
        :selected-index="taskMentions.selectedIndex"
        :tasks-loading="taskMentions.tasksLoading"
        :mention-query="taskMentions.mentionQuery"
        @select="handleTaskMentionSelect"
        @hover="handleTaskMentionHover"
      />
    </div>

    <div v-else class="wysiwyg-wrapper" @keydown="handleWysiwygKeyDown">
      <div class="wysiwyg-toolbar">
        <button
          type="button"
          class="toolbar-btn"
          :disabled="disabled"
          :title="s__('ContextMenu|Bold')"
          :aria-label="s__('ContextMenu|Bold')"
          @click="toggleBold"
        >
          <b>B</b>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          :disabled="disabled"
          :title="s__('ContextMenu|Italic')"
          :aria-label="s__('ContextMenu|Italic')"
          @click="toggleItalic"
        >
          <i>I</i>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          :disabled="disabled"
          :title="s__('ContextMenu|Underline')"
          :aria-label="s__('ContextMenu|Underline')"
          @click="toggleUnderline"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          :disabled="disabled"
          :title="s__('ContextMenu|Strikethrough')"
          :aria-label="s__('ContextMenu|Strikethrough')"
          @click="toggleStrike"
        >
          <s>S</s>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          :disabled="disabled"
          :title="s__('ContextMenu|Code')"
          :aria-label="s__('ContextMenu|Code')"
          @click="toggleCode"
        >
          &lt;/&gt;
        </button>
        <button
          type="button"
          class="toolbar-btn toolbar-btn-mobile-hidden"
          :disabled="disabled"
          :title="s__('ContextMenu|Monospace')"
          :aria-label="s__('ContextMenu|Monospace')"
          @click="toggleCodeBlock"
        >
          {{ s__('ContextMenu|Code') }}
        </button>
        <button
          type="button"
          class="toolbar-btn toolbar-btn-mobile-hidden"
          :disabled="disabled"
          :title="s__('ContextMenu|Quotation')"
          :aria-label="s__('ContextMenu|Quotation')"
          @click="toggleBlockquote"
        >
          {{ s__('ContextMenu|Quotation') }}
        </button>
        <button
          type="button"
          class="toolbar-btn"
          :disabled="disabled"
          :title="s__('ContextMenu|Bullet list')"
          :aria-label="s__('ContextMenu|Bullet list')"
          @click="toggleBulletList"
        >
          <ListUlIcon />
        </button>
        <button
          type="button"
          class="toolbar-btn"
          :disabled="disabled"
          :title="s__('ContextMenu|Link')"
          :aria-label="s__('ContextMenu|Link')"
          @click="setLink"
        >
          <ExternalLinkIcon />
        </button>
        <button
          type="button"
          class="toolbar-btn"
          :disabled="disabled"
          :title="s__('ContextMenu|Clear formatting')"
          :aria-label="s__('ContextMenu|Clear formatting')"
          @click="clearFormatting"
        >
          <ClearNodesIcon />
        </button>
        <slot name="toolbar-extra"></slot>
      </div>
      <EditorContent :editor="editor" class="wysiwyg-editor" @focusin="handleWysiwygFocusIn" />

      <mention-autocomplete
        :show="wysiwygUserMentions.show"
        :position="wysiwygUserMentions.position"
        :filtered-members="wysiwygUserMentionCandidates"
        :selected-index="wysiwygUserMentions.selectedIndex"
        @select="insertWysiwygUserMention"
        @hover="index => (wysiwygUserMentions.selectedIndex = index)"
      />
      <task-autocomplete
        :show="wysiwygTaskMentions.show"
        :position="wysiwygTaskMentions.position"
        :filtered-tasks="wysiwygTaskCandidates"
        :selected-index="wysiwygTaskMentions.selectedIndex"
        :tasks-loading="wysiwygTaskMentionsLoading"
        :mention-query="wysiwygTaskMentions.query"
        @select="insertWysiwygTaskMention"
        @hover="index => (wysiwygTaskMentions.selectedIndex = index)"
      />
    </div>

    <div v-if="clipboardUploadUploading" class="clipboard-upload-state">
      {{ s__('Attachments|Uploading...') }}
    </div>
    <div v-if="clipboardUploadError" class="clipboard-upload-error">
      {{ clipboardUploadError }}
    </div>

    <Teleport to="body">
      <div v-if="showLinkModal" class="modal-overlay" @click.self="cancelLinkModal">
        <div class="modal-content link-modal">
          <div class="modal-header">
            <h3>{{ s__('ContextMenu|Link') }}</h3>
            <button
              type="button"
              class="modal-close"
              :aria-label="s__('TaskDetail|Close')"
              @click="cancelLinkModal"
            >
              ×
            </button>
          </div>
          <div class="modal-body">
            <label class="link-input-label" for="link-url-input">
              {{ s__('ContextMenu|Link URL') }}
            </label>
            <input
              id="link-url-input"
              ref="linkInputRef"
              v-model="linkModalUrl"
              type="url"
              :placeholder="s__('ContextMenu|https://example.com')"
              class="link-input"
              @keydown.enter.prevent="confirmLinkModal"
            />
            <p class="muted link-modal-hint">
              {{ s__('ContextMenu|Leave empty to remove link') }}
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-secondary" @click="cancelLinkModal">
              {{ s__('TaskDetail|Cancel') }}
            </button>
            <button type="button" class="btn-primary" @click="confirmLinkModal">
              {{ s__('TaskDetail|Save') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import { Mark, Node, mergeAttributes } from '@tiptap/core'
import { useMarkdownHotkeys } from '@/composables/useMarkdownHotkeys'
import { useMarkdownContextMenu } from '@/composables/useMarkdownContextMenu'
import { useMentionAutocomplete } from '@/composables/useMentionAutocomplete'
import { useTaskMentionAutocomplete } from '@/composables/useTaskMentionAutocomplete'
import { useClipboardImageUpload } from '@/composables/useClipboardImageUpload'
import { s__ } from '@/locale'
import ExternalLinkIcon from '@/components/common/icons/ExternalLinkIcon.vue'
import ListUlIcon from '@/components/common/icons/ListUlIcon.vue'
import ClearNodesIcon from '@/components/common/icons/ClearNodesIcon.vue'
import MentionAutocomplete from './MentionAutocomplete.vue'
import TaskAutocomplete from './TaskAutocomplete.vue'
import MarkdownContextMenu from './MarkdownContextMenu.vue'
import {
  telegramMarkdownToHtml,
  htmlToTelegramMarkdown,
  normalizeTelegramMarkdown,
} from '@/utils/telegramEditorCodec'

const Spoiler = Mark.create({
  name: 'spoiler',
  parseHTML() {
    return [{ tag: 'span.spoiler' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'spoiler' }), 0]
  },
  addCommands() {
    return {
      toggleSpoiler:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
    }
  },
})

const InlineAttachmentImage = Node.create({
  name: 'inlineAttachmentImage',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
    }
  },

  parseHTML() {
    return [{ tag: 'img[src]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes, { class: 'inline-attachment-image' })]
  },
})

const TASKS_SEARCH_QUERY = gql`
  query SearchTasksForDualEditor($projectId: ID!, $search: String!, $first: Int!) {
    project(id: $projectId) {
      id
      tasks(search: $search, first: $first) {
        edges {
          node {
            id
            iid
            title
            archived
            workflowStatus {
              id
              name
              color
            }
          }
        }
      }
    }
  }
`

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  minHeight: { type: String, default: '120px' },
  disabled: { type: Boolean, default: false },
  enableWysiwyg: { type: Boolean, default: true },
  members: { type: Array, default: () => [] },
  projectId: { type: [String, Number], default: null },
  clipboardUploadEnabled: { type: Boolean, default: false },
  attachmentContext: { type: Object, default: () => ({}) },
  attachmentDraftId: { type: [String, Number], default: null },
})

const emit = defineEmits([
  'update:modelValue',
  'draft-created',
  'upload-started',
  'upload-finished',
  'upload-failed',
])
const currentMode = computed(() => (props.enableWysiwyg ? 'wysiwyg' : 'markdown'))

const editorRootRef = ref(null)
const textareaRef = ref(null)
const linkInputRef = ref(null)
const membersRef = computed(() => props.members || [])
const projectIdRef = computed(() => props.projectId)
const showLinkModal = ref(false)
const linkModalUrl = ref('')
const markdownHotkeys = useMarkdownHotkeys(textareaRef)
const markdownContextMenu = useMarkdownContextMenu(textareaRef)
const userMentions = useMentionAutocomplete(textareaRef, membersRef)
const taskMentions = useTaskMentionAutocomplete(textareaRef, projectIdRef)
const {
  uploading: clipboardUploadUploading,
  error: clipboardUploadError,
  uploadClipboardImage,
} = useClipboardImageUpload()

const handleTextareaInput = event => {
  emit('update:modelValue', event.target.value)
  userMentions.handleInput(event)
  taskMentions.handleInput(event)
}

const handleMarkdownKeyDown = event => {
  if (taskMentions.handleKeyDown(event)) return
  if (userMentions.handleKeyDown(event)) return
  markdownHotkeys.handleKeyDown(event)
}

const handleMarkdownPaste = event => {
  if (handleClipboardImagePaste(event, 'markdown')) return
}

const handleMarkdownFormat = formatType => {
  switch (formatType) {
    case 'bold':
      markdownContextMenu.formatBold()
      break
    case 'italic':
      markdownContextMenu.formatItalic()
      break
    case 'code':
      markdownContextMenu.formatCode()
      break
    case 'strikethrough':
      markdownContextMenu.formatStrikethrough()
      break
    case 'underline':
      markdownContextMenu.formatUnderline()
      break
    case 'monospace':
      markdownContextMenu.formatMonospace()
      break
    case 'spoiler':
      markdownContextMenu.formatSpoiler()
      break
    case 'link':
      markdownContextMenu.formatLink()
      break
    case 'quotation':
      markdownContextMenu.formatQuotation()
      break
  }
}

const handleMarkdownAction = actionType => {
  switch (actionType) {
    case 'cut':
      markdownContextMenu.cut()
      break
    case 'copy':
      markdownContextMenu.copy()
      break
    case 'delete':
      markdownContextMenu.deleteText()
      break
  }
}

const handleUserMentionSelect = member => {
  userMentions.insertMention(member)
}

const handleUserMentionHover = index => {
  userMentions.selectedIndex = index
}

const handleTaskMentionSelect = task => {
  taskMentions.insertTaskMention(task)
}

const handleTaskMentionHover = index => {
  taskMentions.selectedIndex = index
}

const editor = useEditor({
  editable: !props.disabled,
  content: telegramMarkdownToHtml(props.modelValue || ''),
  extensions: [
    StarterKit,
    Underline,
    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
    }),
    Placeholder.configure({
      placeholder: props.placeholder || '',
    }),
    Spoiler,
    InlineAttachmentImage,
  ],
  editorProps: {
    handleKeyDown(_view, event) {
      return handleWysiwygAutocompleteKeyDown(event)
    },
    handlePaste(_view, event) {
      return handleClipboardImagePaste(event, 'wysiwyg')
    },
  },
  onUpdate({ editor: instance }) {
    const markdown = htmlToTelegramMarkdown(instance.getHTML())
    emit('update:modelValue', markdown)
    updateWysiwygMentionState()
  },
})

watch(
  () => props.modelValue,
  value => {
    if (!editor.value) return
    const current = htmlToTelegramMarkdown(editor.value.getHTML())
    const next = normalizeTelegramMarkdown(value || '')
    if (normalizeTelegramMarkdown(current) !== next) {
      editor.value.commands.setContent(telegramMarkdownToHtml(next), false)
    }
  }
)

watch(
  () => props.disabled,
  value => {
    if (!editor.value) return
    editor.value.setEditable(!value)
  }
)

const textareaStyle = computed(() => ({
  width: '100%',
  minHeight: props.minHeight,
}))

const toggleBold = () => editor.value?.chain().focus().toggleBold().run()
const toggleItalic = () => editor.value?.chain().focus().toggleItalic().run()
const toggleUnderline = () => editor.value?.chain().focus().toggleUnderline().run()
const toggleStrike = () => editor.value?.chain().focus().toggleStrike().run()
const toggleCode = () => editor.value?.chain().focus().toggleCode().run()
const toggleCodeBlock = () => editor.value?.chain().focus().toggleCodeBlock().run()
const toggleBlockquote = () => editor.value?.chain().focus().toggleBlockquote().run()
const toggleBulletList = () => editor.value?.chain().focus().toggleBulletList().run()
const clearFormatting = () => editor.value?.chain().focus().unsetAllMarks().clearNodes().run()

const setLink = () => {
  if (!editor.value) return
  linkModalUrl.value = editor.value.getAttributes('link').href || ''
  showLinkModal.value = true
}

const cancelLinkModal = () => {
  showLinkModal.value = false
}

const confirmLinkModal = () => {
  if (!editor.value) return
  const url = String(linkModalUrl.value || '').trim()
  if (!url) {
    editor.value.chain().focus().unsetLink().run()
    showLinkModal.value = false
    return
  }
  editor.value.chain().focus().setLink({ href: url }).run()
  showLinkModal.value = false
}

const wysiwygUserMentions = ref({
  show: false,
  query: '',
  selectedIndex: 0,
  from: 0,
  to: 0,
  position: { x: 0, y: 0 },
})
const wysiwygTaskMentions = ref({
  show: false,
  query: '',
  selectedIndex: 0,
  from: 0,
  to: 0,
  position: { x: 0, y: 0 },
})

const wysiwygTaskQuery = computed(() => {
  if (!wysiwygTaskMentions.value.show) return null
  return wysiwygTaskMentions.value.query || ''
})

const { result: wysiwygTasksResult, loading: wysiwygTaskMentionsLoading } = useQuery(
  TASKS_SEARCH_QUERY,
  () => ({
    projectId: projectIdRef.value || null,
    search: wysiwygTaskQuery.value || '',
    first: 20,
  }),
  () => ({
    enabled:
      currentMode.value === 'wysiwyg' &&
      !!projectIdRef.value &&
      wysiwygTaskMentions.value.show &&
      wysiwygTaskQuery.value !== null,
  })
)

const wysiwygTaskCandidates = computed(() => {
  const edges = wysiwygTasksResult.value?.project?.tasks?.edges || []
  return edges.map(edge => edge.node).filter(task => !task.archived)
})

const wysiwygUserMentionCandidates = computed(() => {
  const query = (wysiwygUserMentions.value.query || '').toLowerCase()
  const members = Array.isArray(props.members) ? props.members : []
  const validMembers = members.filter(member => member && member.username)
  if (!query) return validMembers
  return validMembers.filter(member => {
    const username = String(member.username || '').toLowerCase()
    const name = String(member.name || '').toLowerCase()
    return username.includes(query) || name.includes(query)
  })
})

function hideWysiwygMentionState() {
  wysiwygUserMentions.value.show = false
  wysiwygTaskMentions.value.show = false
}

function updateWysiwygMentionPosition() {
  if (!editor.value) return { x: 0, y: 0 }
  const coords = editor.value.view.coordsAtPos(editor.value.state.selection.from)
  return { x: Math.max(8, coords.left), y: coords.bottom + 4, positionAbove: false }
}

function updateWysiwygMentionState() {
  if (!editor.value || currentMode.value !== 'wysiwyg') {
    hideWysiwygMentionState()
    return
  }

  const selectionFrom = editor.value.state.selection.from
  const before = editor.value.state.doc.textBetween(0, selectionFrom, '\n', '\n')

  const userMatch = before.match(/(?:^|\s)@([a-zA-Z0-9_]*)$/)
  if (userMatch) {
    const query = userMatch[1] || ''
    const tokenLength = query.length + 1
    wysiwygUserMentions.value = {
      ...wysiwygUserMentions.value,
      show: true,
      query,
      selectedIndex: 0,
      from: selectionFrom - tokenLength,
      to: selectionFrom,
      position: updateWysiwygMentionPosition(),
    }
    wysiwygTaskMentions.value.show = false
    return
  }

  const taskMatch = before.match(/(?:^|\s)#(\d*)$/)
  if (taskMatch) {
    const query = taskMatch[1] || ''
    const tokenLength = query.length + 1
    wysiwygTaskMentions.value = {
      ...wysiwygTaskMentions.value,
      show: true,
      query,
      selectedIndex: 0,
      from: selectionFrom - tokenLength,
      to: selectionFrom,
      position: updateWysiwygMentionPosition(),
    }
    wysiwygUserMentions.value.show = false
    return
  }

  hideWysiwygMentionState()
}

function insertWysiwygUserMention(member) {
  if (!editor.value || !member?.username) return
  const state = wysiwygUserMentions.value
  editor.value
    .chain()
    .focus()
    .deleteRange({ from: state.from, to: state.to })
    .insertContent(`@${member.username} `)
    .run()
  hideWysiwygMentionState()
}

function insertWysiwygTaskMention(task) {
  if (!editor.value || !task?.iid) return
  const state = wysiwygTaskMentions.value
  editor.value
    .chain()
    .focus()
    .deleteRange({ from: state.from, to: state.to })
    .insertContent(`#${task.iid} `)
    .run()
  hideWysiwygMentionState()
}

function handleWysiwygAutocompleteKeyDown(event) {
  if (wysiwygUserMentions.value.show) {
    const candidates = wysiwygUserMentionCandidates.value
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      event.stopPropagation()
      wysiwygUserMentions.value.selectedIndex = Math.min(
        wysiwygUserMentions.value.selectedIndex + 1,
        candidates.length - 1
      )
      return true
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      event.stopPropagation()
      wysiwygUserMentions.value.selectedIndex = Math.max(
        wysiwygUserMentions.value.selectedIndex - 1,
        0
      )
      return true
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      const selected = candidates[wysiwygUserMentions.value.selectedIndex]
      if (selected) {
        event.preventDefault()
        event.stopPropagation()
        insertWysiwygUserMention(selected)
        return true
      }
      return false
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      hideWysiwygMentionState()
      return true
    }
  }

  if (wysiwygTaskMentions.value.show) {
    const candidates = wysiwygTaskCandidates.value
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      event.stopPropagation()
      wysiwygTaskMentions.value.selectedIndex = Math.min(
        wysiwygTaskMentions.value.selectedIndex + 1,
        candidates.length - 1
      )
      return true
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      event.stopPropagation()
      wysiwygTaskMentions.value.selectedIndex = Math.max(
        wysiwygTaskMentions.value.selectedIndex - 1,
        0
      )
      return true
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      const selected = candidates[wysiwygTaskMentions.value.selectedIndex]
      if (selected) {
        event.preventDefault()
        event.stopPropagation()
        insertWysiwygTaskMention(selected)
        return true
      }
      return false
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      hideWysiwygMentionState()
      return true
    }
  }

  return false
}

function handleWysiwygKeyDown(event) {
  if (handleWysiwygAutocompleteKeyDown(event)) {
    return
  }

  Promise.resolve().then(() => updateWysiwygMentionState())
}

function handleWysiwygFocusIn() {
  if (!window.matchMedia('(max-width: 768px)').matches) return
  const root = editorRootRef.value
  if (!root) return
  const rect = root.getBoundingClientRect()
  const targetTop = window.scrollY + rect.top + rect.height / 2 - window.innerHeight / 2
  window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
}

function clipboardImageFiles(event) {
  if (!props.clipboardUploadEnabled || props.disabled) return []

  const items = Array.from(event.clipboardData?.items || [])
  return items
    .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
    .map(item => item.getAsFile())
    .filter(Boolean)
}

function uploadContext() {
  return {
    ...(props.attachmentContext || {}),
    attachmentDraftId:
      props.attachmentDraftId || props.attachmentContext?.attachmentDraftId || null,
  }
}

function placeholderFor(file) {
  const name = file.name || s__('Attachments|Images')
  return `[${s__('Attachments|Uploading...')} ${name}]`
}

function insertMarkdownAtCursor(textarea, text) {
  const value = textarea.value || ''
  const start = textarea.selectionStart || 0
  const end = textarea.selectionEnd || start
  const nextValue = `${value.slice(0, start)}${text}${value.slice(end)}`
  emit('update:modelValue', nextValue)
  nextTick(() => {
    textarea.focus()
    const position = start + text.length
    textarea.setSelectionRange(position, position)
  })
  return nextValue
}

function replaceMarkdownText(currentText, placeholder, replacement) {
  const index = currentText.indexOf(placeholder)
  if (index === -1) return `${currentText}\n${replacement}`
  return `${currentText.slice(0, index)}${replacement}${currentText.slice(index + placeholder.length)}`
}

function handleClipboardImagePaste(event, mode) {
  const files = clipboardImageFiles(event)
  if (!files.length) return false

  event.preventDefault()
  event.stopPropagation()
  processClipboardImages(files, mode)

  return true
}

async function processClipboardImages(files, mode) {
  for (const file of files) {
    const placeholder = placeholderFor(file)
    if (mode === 'wysiwyg') {
      editor.value?.chain().focus().insertContent(`${placeholder} `).run()
    } else if (textareaRef.value) {
      insertMarkdownAtCursor(textareaRef.value, `${placeholder} `)
    }

    emit('upload-started', { file })

    try {
      const result = await uploadClipboardImage(file, uploadContext())
      if (result.attachmentDraftId && result.attachmentDraftId !== props.attachmentDraftId) {
        emit('draft-created', result.attachmentDraftId)
      }

      const currentMarkdown =
        mode === 'wysiwyg'
          ? htmlToTelegramMarkdown(editor.value?.getHTML() || '')
          : textareaRef.value?.value || props.modelValue || ''
      const nextMarkdown = replaceMarkdownText(currentMarkdown, placeholder, result.markdown)

      if (mode === 'wysiwyg') {
        editor.value?.commands.setContent(telegramMarkdownToHtml(nextMarkdown), false)
      }
      emit('update:modelValue', nextMarkdown)
      emit('upload-finished', result)
    } catch (uploadError) {
      emit('upload-failed', uploadError)
    }
  }
}

watch(showLinkModal, async value => {
  if (!value) return
  await nextTick()
  linkInputRef.value?.focus()
  linkInputRef.value?.select()
})
</script>

<style scoped>
.telegram-dual-editor {
  width: 100%;
}

.clipboard-upload-state,
.clipboard-upload-error {
  margin-top: 6px;
  font-size: 12px;
}

.clipboard-upload-state {
  color: #666;
}

.clipboard-upload-error {
  color: #dc2626;
}

.link-modal {
  max-width: 440px;
}

.link-input-label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}

.link-input {
  width: 100%;
}

.link-modal-hint {
  margin: 8px 0 0;
}

.wysiwyg-wrapper {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.wysiwyg-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.toolbar-btn {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  background: #fff;
  cursor: pointer;
  color: #333;
  font-size: 12px;
}

.toolbar-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #cbd5e1;
  color: #333;
}

.toolbar-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

:slotted(.toolbar-btn) {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  background: #fff;
  cursor: pointer;
  color: #333;
  font-size: 12px;
  line-height: 1.2;
}

:slotted(.toolbar-btn:hover:not(:disabled)) {
  background: #f3f4f6;
  border-color: #cbd5e1;
  color: #333;
}

.wysiwyg-editor {
  min-height: 140px;
  padding: 10px 12px;
  color: #333;
}

:deep(.wysiwyg-editor .ProseMirror) {
  min-height: 120px;
  outline: none;
  white-space: pre-wrap;
}

:deep(.wysiwyg-editor .ProseMirror pre) {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 12px;
  margin: 8px 0;
  overflow-x: auto;
}

:deep(.wysiwyg-editor .ProseMirror pre code) {
  background: transparent;
  border: none;
  padding: 0;
  color: #1f2937;
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
}

:deep(.wysiwyg-editor .ProseMirror blockquote) {
  border-left: 3px solid #d1d5db;
  background: #f8fafc;
  margin: 8px 0;
  padding: 8px 12px;
  color: #4b5563;
}

:deep(.wysiwyg-editor .spoiler) {
  background: #222;
  color: #222;
  border-radius: 3px;
  padding: 0 2px;
}

:deep(.wysiwyg-editor .spoiler:hover) {
  color: #fff;
}

:deep(.wysiwyg-editor .user-mention-text) {
  color: #7c3aed;
  font-weight: 600;
}

:deep(.wysiwyg-editor .inline-attachment-image) {
  display: block;
  max-width: 100%;
  max-height: 420px;
  margin: 10px 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  object-fit: contain;
}

@media (max-width: 768px) {
  .toolbar-btn-mobile-hidden {
    display: none;
  }
}
</style>
